import { logger } from 'utils/logger';

const clone = structuredClone;
type AnyObject = { [prop: string]: any };

export function getEntryBy<T extends AnyObject>(
  uniqueValue: string | undefined,
  elements: T[],
  uniqueValueKey: string = 'uid'
): T | undefined {
  return elements.find((entry) => entry[uniqueValueKey] === uniqueValue);
}

function getIndexBy<T extends AnyObject>(uniqueValue: string, elements: T[], uniqueValueKey: string = 'uid'): number {
  return elements.findIndex((entry) => entry?.[uniqueValueKey] === uniqueValue);
}

export function getItemByKey<T extends { key?: string }, K extends T['key']>(key: K, items: T[]): T | undefined {
  return getEntryBy(key, items, 'key');
}

export function getItemByReferenceIdAndKey<T extends {}, K extends string>(
  item: {
    key: K;
    referenceId?: string | undefined;
  },
  options: T[]
): T | undefined {
  return options.find((option) => {
    return (
      (option as { referenceId?: string }).referenceId === item.referenceId &&
      (option as { key?: string }).key === item.key
    );
  });
}

export function modifyProperty<T extends AnyObject, KEY extends keyof T = 'uid'>(
  uniqueValues: string[],
  propertyNameToReplace: KEY,
  targetValue: T[KEY],
  elements: T[] | null,
  uniqueValueKey: string = 'uid'
): T[] {
  if (!elements) {
    return [];
  }
  const data = clone(elements);
  uniqueValues.forEach((uid) => {
    const entry = getEntryBy(uid, data, uniqueValueKey);
    if (entry) {
      entry[propertyNameToReplace] = targetValue;
    }
  });
  return [...data];
}

export function removeElements<T extends AnyObject>(
  uniqueValues: string[],
  entries: T[],
  uniqueValueKey: string = 'uid'
): T[] {
  const result: T[] = clone(entries);
  uniqueValues.forEach((uid) => result.splice(getIndexBy(uid, entries, uniqueValueKey), 1));
  return result;
}

export function removeElementByIndex<T extends AnyObject>(entries: T[], index: number): T[] {
  const result: T[] = clone(entries);
  result.splice(index, 1);
  return result;
}

export function replaceElement<T extends { [prop: string]: any }>(
  model: T,
  entries: T[],
  uniqueValueKey: string = 'uid'
): T[] {
  let result: T[];
  try {
    result = clone(entries);
  } catch (e) {
    result = [...entries];
  }

  if (isPropertyOf(model, uniqueValueKey)) {
    const index = getIndexBy(model[uniqueValueKey], entries, uniqueValueKey);
    if (index !== -1) {
      result.splice(index, 1, clone(model));
    }
  } else {
    logger.error(`No "${uniqueValueKey}" property in object: ${JSON.stringify(model)}`);
  }
  return result;
}

export function replaceElementByKey<T extends { [prop: string]: any }>(
  model: T,
  entries: T[],
  value: string,
  uniqueValueKey: string = 'uid'
): T[] {
  let result: T[];
  try {
    result = clone(entries);
  } catch (e) {
    result = [...entries];
  }
  if (!isPropertyOf(model, uniqueValueKey)) {
    logger.error(`No "${uniqueValueKey}" property in object: ${JSON.stringify(model)}`);
  }
  const index = entries.findIndex((entry) => {
    return entry[uniqueValueKey] === value;
  });
  if (index !== -1) {
    result.splice(index, 1, clone(model));
  }
  return result;
}

export function replaceElementWith<T extends { [prop: string]: any }>(
  element: T,
  replacement: T,
  elements: T[],
  propName: keyof Required<T> & string
): T[] {
  let result: T[];
  try {
    result = clone(elements);
  } catch (e) {
    result = [...elements];
  }

  const index = result.findIndex((entry) => {
    return entry[propName] === element[propName];
  });

  if (index !== -1) {
    result.splice(index, 1, clone(replacement));
  }
  return result;
}

export function moveElement<T>(
  entries: (T | undefined | null)[],
  sourceIndex: number,
  destinationIndex: number
): (T | undefined | null)[] {
  const result = Array.from(entries);
  const [removed] = result.splice(sourceIndex, 1);
  result.splice(destinationIndex, 0, removed);
  return result;
}

export function moveElements<T>(
  entries: (T | undefined | null)[],
  sourceIndex: number,
  howMany: number,
  destinationIndex: number
): (T | undefined | null)[] {
  const result = Array.from(entries);
  const removed = result.splice(sourceIndex, howMany);
  result.splice(destinationIndex, 0, ...removed);
  return result;
}

function isPropertyOf<T extends {}, K extends keyof T>(model: T, uniqueValueKey: any): uniqueValueKey is K {
  return !!model[uniqueValueKey as keyof typeof model];
}

export function replaceElementByIndex<T>(model: T, entries: T[], index: number): T[] {
  const result: T[] = clone(entries);
  if (index !== -1) {
    result.splice(index, 1, clone(model));
  }
  return result;
}

export function withUids<MODEL extends { uid?: string | null }>(
  values: (MODEL | undefined | null | false)[] | null | undefined
) {
  return filterWithProp(values, 'uid');
}

export function filterWithProp<MODEL extends { [p: string]: any }, K extends keyof MODEL & string>(
  values: (MODEL | undefined | null | false)[] | null | undefined,
  key: K
): (Omit<MODEL, K> & { [prop in K]: Exclude<MODEL[K], undefined | null> })[] {
  return filterNotEmpty(values).filter((it) => !!it[key]);
}

interface FilterNotEmptyOptions {
  removeEmptyStrings?: boolean;
  removeEmptyArrays?: boolean;
}
export function filterNotEmpty<MODEL>(
  values: (MODEL | undefined | null | false)[] | undefined | null,
  options?: FilterNotEmptyOptions
): MODEL[] {
  return (values ?? []).filter(notEmpty(options));
}

export function notEmpty<MODEL>(
  options: FilterNotEmptyOptions = {}
): (value: MODEL | null | undefined | false) => value is MODEL {
  // @ts-ignore TS doesn't like nested assertions
  return (value) => {
    const result = !(
      value === null ||
      value === undefined ||
      value === false ||
      (options?.removeEmptyStrings && typeof value === 'string' && value.trim() === '') ||
      (options?.removeEmptyArrays && Array.isArray(value) && value?.length === 0)
    );
    return result;
  };
}

// Remove duplicates from array of objects
export const arrayUniqueByKey = <T extends AnyObject, K extends keyof T>(array: T[], key: K): T[] => [
  ...new Map(array.map((item) => [item[key], item])).values()
];

export const flattenList = <T extends { children: T[] }>(array: T[] = []) => {
  let result: T[] = [];
  array.forEach((a) => {
    result.push(a);
    if (Array.isArray(a.children)) {
      result = result.concat(flattenList(a.children));
    }
  });
  return result;
};

export function groupBy<T extends object, K extends keyof T & (string | number) = keyof T & (string | number)>(
  values: T[],
  key: K
): Record<T[K] & (string | number), T[]>;
export function groupBy<T extends object, R extends string | number>(values: T[], cb: (item: T) => R): Record<R, T[]>;
export function groupBy<T extends object>(values: T[], keyOrCb: string | ((item: T) => string)): Record<string, T[]> {
  return values.reduce(
    (acc, curr) => {
      const key = typeof keyOrCb === 'string' ? curr[keyOrCb as keyof typeof curr] : keyOrCb(curr);
      const accValue = acc[key as keyof typeof acc] ?? [];
      return {
        ...acc,
        [key as string]: [...accValue, curr]
      };
    },
    {} as Record<string, T[]>
  );
}

// Helper type for filtering keys where the value is a number
type IsNumberKey<Obj, Key extends keyof Obj> = Obj[Key] extends number ? Key : never;

// Main type: Filters only the keys of Obj that map to numbers
type FilterNumberKeys<Obj extends object> = {
  [Key in keyof DeeplyDefined<Obj>]: Key extends string ? IsNumberKey<DeeplyDefined<Obj>, Key> : never;
}[keyof Obj];

export function sumBy<T extends object, K extends FilterNumberKeys<T> = FilterNumberKeys<T>>(
  values: T[],
  key: K,
  sumFrom = 0
): number {
  return values.reduce((acc, curr) => acc + ((curr[key] ?? 0) as number), sumFrom);
}
